import {
  ApiEnrichScheduleFields,
  ApiList,
  ApiRefreshScheduleFields,
  IRefreshListOutput,
  ScheduleValue,
  SourcingList,
  SourcingSavedSearch,
  refreshListOutputSchema,
  sourcingListSchema,
  sourcingSavedSearchSchema,
} from '../../schemas/SourcingList.schema';
import {
  IAddCompanyToListRequest,
  IAddPersonToListRequest,
  IEnrichCompanyRequest,
  IEnrichmentResponse,
  IEnrichPersonRequest,
  IApiCompanyRow,
  IApiPersonRow,
  ISourcingCompanyRow,
  ISourcingPersonRow,
  sourcingCompanyRowSchema,
  sourcingPersonRowSchema,
} from '../../schemas/SourcingListItems.schema';
import { buildSchedulePayload } from '../../pages/Sourcing/schedule/schedule';
import { MaggieHttp } from '../MaggieHttp';

export async function getSourcingListsAndSavedSearches(): Promise<{
  lists: SourcingList[];
  savedSearches: SourcingSavedSearch[];
}> {
  const { data } = await MaggieHttp.sourcing.get<ApiList[]>('/api/v1/lists');
  const lists = data.filter((l) => l.type === 'STATIC').map((l) => sourcingListSchema.cast(l));
  const savedSearches = data.filter((l) => l.type === 'ACTIVE').map((l) => sourcingSavedSearchSchema.cast(l));
  return { lists, savedSearches };
}

export async function createSourcingList(
  name: string,
  entityType: 'company' | 'person'
): Promise<SourcingList> {
  const { data } = await MaggieHttp.sourcing.post<
    { shortName: string; entityType: 'company' | 'person'; type: 'STATIC' },
    ApiList
  >('/api/v1/lists', { shortName: name, entityType, type: 'STATIC' });
  return sourcingListSchema.cast(data);
}

export async function renameSourcingList(id: string, name: string): Promise<SourcingList> {
  const { data } = await MaggieHttp.sourcing.put<{ shortName: string }, ApiList>(`/api/v1/lists/${id}`, {
    shortName: name,
  });
  return sourcingListSchema.cast(data);
}

export async function deleteSourcingList(id: string): Promise<void> {
  await MaggieHttp.sourcing.delete(`/api/v1/lists/${id}`);
}

export async function duplicateSourcingList(id: string, shortName?: string): Promise<SourcingList> {
  const { data } = await MaggieHttp.sourcing.post<{ shortName?: string }, ApiList>(
    `/api/v1/lists/${id}/duplicate`,
    shortName ? { shortName } : {}
  );
  return sourcingListSchema.cast(data);
}

export async function bulkRemoveListItems(listId: string, itemIds: string[]): Promise<{ removed: number }> {
  const { data } = await MaggieHttp.sourcing.post<{ itemIds: string[] }, { removed: number }>(
    `/api/v1/lists/${listId}/items/bulk-remove`,
    { itemIds }
  );
  return data;
}

export async function fetchListCompanies(
  listId: string,
  signal?: AbortSignal
): Promise<ISourcingCompanyRow[]> {
  const { data } = await MaggieHttp.sourcing.get<IApiCompanyRow[]>(`/api/v1/lists/${listId}/companies`, {
    signal,
  });
  return data.map((c) => sourcingCompanyRowSchema.cast(c));
}

export async function fetchListPeople(listId: string, signal?: AbortSignal): Promise<ISourcingPersonRow[]> {
  const { data } = await MaggieHttp.sourcing.get<IApiPersonRow[]>(`/api/v1/lists/${listId}/people`, {
    signal,
  });
  return data.map((p) => sourcingPersonRowSchema.cast(p));
}

export async function createSourcingSavedSearch(
  name: string,
  entityType: 'company' | 'person',
  query?: string,
  schedule?: ScheduleValue
): Promise<SourcingSavedSearch> {
  const scheduleFields = schedule !== undefined ? buildSchedulePayload('refresh', schedule) : {};
  const { data } = await MaggieHttp.sourcing.post<
    {
      shortName: string;
      entityType: 'company' | 'person';
      type: 'ACTIVE';
      query?: string;
    } & ApiRefreshScheduleFields,
    ApiList
  >('/api/v1/lists', {
    shortName: name,
    entityType,
    type: 'ACTIVE',
    ...(query ? { query } : {}),
    ...scheduleFields,
  });
  return sourcingSavedSearchSchema.cast(data);
}

export async function addCompaniesToList(
  listId: string,
  companies: IAddCompanyToListRequest[]
): Promise<void> {
  await MaggieHttp.sourcing.post<{ companies: IAddCompanyToListRequest[] }, void>(
    `/api/v1/lists/${listId}/companies`,
    { companies }
  );
}

export async function addPeopleToList(listId: string, people: IAddPersonToListRequest[]): Promise<void> {
  await MaggieHttp.sourcing.post<{ people: IAddPersonToListRequest[] }, void>(
    `/api/v1/lists/${listId}/people`,
    { people }
  );
}

export async function copyItemsToLists(itemIds: string[], targetListIds: string[]): Promise<void> {
  await MaggieHttp.sourcing.post<{ itemIds: string[]; targetListIds: string[] }, void>(
    '/api/v1/lists/copy-items',
    { itemIds, targetListIds }
  );
}

/**
 * Add the same set of entities to multiple lists in a single round trip.
 * For company lists `entityIds` are domains; for person lists they are
 * LinkedIn URLs. Each target list is processed according to its own
 * `entityType` server-side, so it is safe to mix list types in `listIds`
 * — non-matching entities are simply ignored.
 *
 * Used by the profile-page "Add to List" flow: one company → many lists.
 */
export async function bulkAddListItems(entityIds: string[], listIds: string[]): Promise<void> {
  await MaggieHttp.sourcing.post<{ entityIds: string[]; listIds: string[] }, void>(
    '/api/v1/lists/bulk-add-items',
    { entityIds, listIds }
  );
}

export async function enrichCompanies(companies: IEnrichCompanyRequest[]): Promise<IEnrichmentResponse> {
  const { data } = await MaggieHttp.sourcing.post<IEnrichCompanyRequest[], IEnrichmentResponse>(
    '/api/v1/companies/enrich',
    companies
  );
  return data;
}

export async function enrichPersons(persons: IEnrichPersonRequest[]): Promise<IEnrichmentResponse> {
  const { data } = await MaggieHttp.sourcing.post<IEnrichPersonRequest[], IEnrichmentResponse>(
    '/api/v1/persons/enrich',
    persons
  );
  return data;
}

export async function updateListSchedule(
  list: SourcingList | SourcingSavedSearch,
  schedule: ScheduleValue
): Promise<SourcingList | SourcingSavedSearch> {
  const slice = list.listType === 'active' ? 'refresh' : 'enrich';
  const payload = buildSchedulePayload(slice, schedule);
  const { data } = await MaggieHttp.sourcing.put<ApiRefreshScheduleFields | ApiEnrichScheduleFields, ApiList>(
    `/api/v1/lists/${list.id}`,
    payload
  );
  return list.listType === 'active' ? sourcingSavedSearchSchema.cast(data) : sourcingListSchema.cast(data);
}

export async function refreshListNow(listId: string): Promise<IRefreshListOutput> {
  const { data } = await MaggieHttp.sourcing.post<undefined, IRefreshListOutput>(
    `/api/v1/lists/${listId}/refresh`
  );
  return refreshListOutputSchema.cast(data);
}
